import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";

/**
 * Simple organization autocomplete input, backed by /api/organizations.
 * Free-text — allows typing any new value; suggests matches from existing users/leads.
 */
export default function OrgAutocomplete({ value, onChange, testId = "org-autocomplete", placeholder = "" }) {
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/organizations");
        if (alive) setOptions(data);
      } catch {
        if (alive) setOptions([]);
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const q = (value || "").toLowerCase().trim();
  const filtered = q
    ? options.filter((o) => o.toLowerCase().includes(q)).slice(0, 8)
    : options.slice(0, 8);

  return (
    <div ref={wrapRef} className="relative">
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onFocus={() => setOpen(true)}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        data-testid={testId}
        autoComplete="off"
        className="w-full px-3.5 py-3 border border-[var(--line)] bg-[#fafafa] text-[15px] text-[var(--ink)] placeholder:text-neutral-400 focus:bg-white focus:border-[var(--navy)] focus:outline-none focus:ring-2 focus:ring-[var(--gold)]/40 transition-colors"
      />
      {open && filtered.length > 0 && (
        <ul
          className="absolute left-0 right-0 top-full z-30 bg-white border border-[var(--line)] max-h-56 overflow-y-auto shadow-lg"
          data-testid={`${testId}-list`}
        >
          {filtered.map((o) => (
            <li
              key={o}
              className="px-3 py-2 text-sm cursor-pointer hover:bg-[var(--cream)]"
              onMouseDown={(e) => { e.preventDefault(); onChange(o); setOpen(false); }}
              data-testid={`${testId}-opt-${o.replace(/\s+/g, "-")}`}
            >
              {o}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
